import React from 'react';
import { REPLY_VERDICT_LABEL, isRefusal } from '../reply';
import type { KeypadLink, KeypadReply } from '../reply';

export interface DeviceReplyProps {
  /** The last thing the device said about a mapping, or null when it is silent. */
  readonly reply: KeypadReply | null;
  /** What the console's link knows about itself, for when `reply` is null. */
  readonly link: KeypadLink;
}

/**
 * The topic answers arrive on, or the words for not knowing it yet.
 *
 * A tablet with no serial number has no topic to name, and naming a malformed
 * one (`/toCloudRCU`) would send the technician looking for the wrong fault.
 */
const AnswerTopic: React.FC<{ readonly link: KeypadLink }> = ({ link }) =>
  link.answerTopic === '' ? (
    <>the device's answer topic</>
  ) : (
    <span className="font-mono text-[#333333]">{link.answerTopic}</span>
  );

/**
 * The two reasons a reply block can be empty, told apart.
 *
 * The second one is the trap: with the transport on automatic, a tablet whose
 * broker session never came up still delivers over HTTP, so writes land and are
 * carried out while nothing the device answers can reach the console at all.
 */
const Silence: React.FC<{ readonly link: KeypadLink }> = ({ link }) => {
  if (!link.listening) {
    return (
      <p className="text-sm text-[#666666]">
        Nothing the device answers can arrive: answers come back on <AnswerTopic link={link} /> and
        this tablet is not subscribed to it. The link's own reason: {link.trouble} Commands can
        still leave, which is what makes a write look as though it worked; what cannot happen is the
        device being heard. The broker settings in the Link sheet are what bring the answers in.
      </p>
    );
  }

  return (
    <p className="text-sm text-[#666666]">
      The link is subscribed to <AnswerTopic link={link} /> and the device has not answered anything
      about mappings since this tablet connected. The event feed at the bottom of the console holds
      everything that has crossed the link.
    </p>
  );
};

/** The time a reply arrived, as a technician reads a clock. */
const arrival = (at: string): string => {
  const moment = new Date(at);
  return Number.isNaN(moment.getTime()) ? at : moment.toLocaleTimeString();
};

/**
 * The device's own words about the last mapping it was asked about.
 *
 * Shown as text on purpose. The firmware does not correlate a reply to the
 * request that caused it, so anything this console wrote here would be a summary
 * of a message it cannot prove belongs to the command the technician just sent —
 * and when a mapping does not take, the thing worth reading is the reply itself.
 */
export const DeviceReply: React.FC<DeviceReplyProps> = ({ reply, link }) => {
  if (reply === null) return <Silence link={link} />;

  return (
    <div className="space-y-2">
      <p className={`text-sm ${isRefusal(reply) ? 'text-[#C0392B]' : 'text-[#333333]'}`}>
        {REPLY_VERDICT_LABEL[reply.verdict]}
      </p>
      <p className="text-xs text-[#666666]">
        {arrival(reply.at)} · {reply.topic === '' ? 'no topic recorded' : reply.topic}
      </p>
      <pre className="max-h-40 overflow-auto rounded-xl bg-[#1A1A1A] p-4 text-xs leading-relaxed text-[#E8E8E8]">
        {reply.text}
      </pre>
    </div>
  );
};

export default DeviceReply;
